import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission, MENU } from 'shared/lib/permissions';
import { Table } from 'shared/ui/table';
import Modal from 'shared/ui/modal';
import ModalDelete from 'shared/ui/modal-delete';
import RowActionsPopover from 'shared/ui/row-actions-popover';
import { CustomButton } from 'shared/ui/custom-button';
import { DeleteIcon, EditIcon, EyeIcon, PlusIcon } from 'shared/ui/icons';
import { useRoleList } from '../../hooks/useRoleList';
import useDeleteRole from '../../hooks/useDeleteRole';
import { RoleDetailSection } from '../role-detail-section';
import { getRoleColumns, SearchIcon } from './role-list-panel.columns';

const RoleListPanel = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const {
    data,
    isFetching,
    refetch,
    page,
    pageSize,
    searchRaw,
    detailModal,
    deleteModal,
    handlePageSizeChange,
    handlePaginationChange,
    handleSearchChange,
  } = useRoleList();

  const { canCreate, canUpdate, canDelete } = usePermission(
    MENU.ROLE_MANAGEMENT
  );

  const { deleteMutation } = useDeleteRole({
    onSuccess: () => {
      deleteModal.closeModal();
      refetch();
    },
  });

  const columns = getRoleColumns(t);

  const rows = (data?.data ?? []).map((item) => ({
    role_name: (
      <div className="w-auto min-w-24 text-nowrap w-fit">{item?.role}</div>
    ),
    roles: (
      <div className="flex gap-1 flex-wrap">
        {item?.menus?.map((menu, i) => (
          <span
            className="inline-block px-2 py-1 rounded-full text-primary-500 bg-primary-50 text-xs font-medium text-nowrap"
            key={i}
          >
            {menu.name}
          </span>
        ))}
      </div>
    ),
    actions: (
      <RowActions
        item={item}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onDetail={() => detailModal.openModal(item)}
        onEdit={() => navigate(`/management-role/edit-role/${item.id}`)}
        onDelete={() => deleteModal.openModal(item)}
        t={t}
      />
    ),
  }));

  return (
    <>
      <div className="role-management w-full bg-white border rounded-lg flex flex-wrap p-6">
        <SearchAndActions
          searchRaw={searchRaw}
          canCreate={canCreate}
          onSearchChange={handleSearchChange}
          onAdd={() => navigate('/management-role/create-role')}
          t={t}
        />

        <div className="w-full mt-6 role-management-table">
          <Table
            data={rows}
            columns={columns}
            pageSize={pageSize}
            loading={isFetching}
            page={data?.meta?.page ?? page}
            total={data?.meta?.total ?? 0}
            limit={data?.meta?.limit ?? pageSize}
            handlePageSizeChange={handlePageSizeChange}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </div>

      <DetailModal detailModal={detailModal} t={t} />
      <DeleteModalSection
        deleteModal={deleteModal}
        deleteMutation={deleteMutation}
      />
    </>
  );
};

function SearchAndActions({ searchRaw, canCreate, onSearchChange, onAdd, t }) {
  return (
    <div className="w-full flex flex-wrap">
      <div className="w-1/2 flex items-center gap-2">
        <div className="relative w-1/2 mr-2">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3">
            {SearchIcon}
          </div>
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base text-neutral-500 font-normal min-w-[320px]"
            placeholder={t('search')}
            value={searchRaw}
            onChange={onSearchChange}
            aria-label={t('search')}
          />
        </div>
      </div>

      {canCreate ? (
        <div className="w-1/2 flex justify-end">
          <CustomButton
            type="primary"
            size="sm"
            onClick={onAdd}
            icon={<PlusIcon color="white" />}
          >
            {t('create_role')}
          </CustomButton>
        </div>
      ) : null}
    </div>
  );
}

function RowActions({
  item,
  canUpdate,
  canDelete,
  onDetail,
  onEdit,
  onDelete,
  t,
}) {
  return (
    <RowActionsPopover>
      <div className="w-full flex flex-wrap p-2 gap-y-2">
        <button
          type="button"
          className="w-full flex flex-wrap items-center px-2 gap-2 cursor-pointer"
          onClick={onDetail}
          aria-label={`View detail for ${item?.role}`}
        >
          <EyeIcon width="18" /> <span className="text-sm">Detail</span>
        </button>

        {canUpdate ? (
          <button
            type="button"
            className="w-full flex flex-wrap items-center px-2 gap-2 cursor-pointer"
            onClick={onEdit}
            aria-label={`Edit ${item?.role}`}
          >
            <EditIcon width="18" /> <span className="text-sm">{t('edit')}</span>
          </button>
        ) : null}

        {canDelete ? (
          <button
            type="button"
            className="w-full flex flex-wrap items-center px-2 gap-2 border-t pt-2 cursor-pointer"
            onClick={onDelete}
            aria-label={`Delete ${item?.role}`}
          >
            <DeleteIcon width="18" />{' '}
            <span className="text-sm">{t('delete')}</span>
          </button>
        ) : null}
      </div>
    </RowActionsPopover>
  );
}

function DetailModal({ detailModal, t }) {
  return detailModal.isOpen ? (
    <Modal
      title={`Detail ${t('role')}`}
      isOpen={detailModal.isOpen}
      setIsOpen={() => detailModal.closeModal()}
      footer={false}
    >
      <RoleDetailSection data={detailModal.modalData} />
    </Modal>
  ) : null;
}

function DeleteModalSection({ deleteModal, deleteMutation }) {
  return deleteModal.isOpen ? (
    <ModalDelete
      setIsModalOpenDelete={(val) => !val && deleteModal.closeModal()}
      handleDelete={() => deleteMutation.mutate(deleteModal.modalData?.id)}
      isModalOpenDelete={deleteModal.isOpen}
      name={deleteModal.modalData?.role}
      isLoading={deleteMutation.isLoading}
    />
  ) : null;
}

export default RoleListPanel;
